import React from "react";
import ExcelJS from "exceljs";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

const ExportParticipantesExcel = ({ data }) => {
  
  const handleExport = async () => {
    if (!data || data.length === 0) return;

    // 1. Detectar asistencias únicas
    const allAsistencias = Array.from(
      new Map(
        data.flatMap(p =>
          p.children
            ? p.children.map(c => [c.oid_asistencia, c.nombre])
            : []
        )
      ).entries()
    )
    .sort((a, b) => a[0] - b[0])
    .map(entry => entry[1]);

    // 2. Definir columnas base con preguntas e incidencias
    const baseHeaders = [
      { key: "nombre_apellido", header: "Nombre y Apellido" },
      { key: "nombre_tipo_documento", header: "Tipo Documento" },
      { key: "numero_documento", header: "Número Documento" },
      { key: "correo", header: "Correo" },
      { key: "telefono", header: "Teléfono" },
      { key: "nombre_sexo", header: "Sexo" },
      { key: "edad", header: "Edad" },
      { key: "etareo", header: "Grupo Etáreo" },
      { key: "municipio", header: "Municipio" },
      { key: "departamento", header: "Departamento" },
      { key: "subRegion", header: "Subregión" },
      { key: "vereda", header: "Vereda" },
      { key: "etnico", header: "Grupo Étnico" },
      { key: "organizacion", header: "Organización" },
      { key: "Asistencias", header: "Total Asistencias" },
      
      // Preguntas
      { key: "pregunta_1", header: "¿Se considera campesino/a?" },
      { key: "pregunta_2", header: "¿Condición de discapacidad?" },
      { key: "pregunta_3", header: "¿Víctima conflicto armado?" },
      { key: "pregunta_4", header: "¿Vinculado al proyecto?" },
      { key: "pregunta_5", header: "¿Requiere apoyo económico transporte?" },
      { key: "pregunta_6", header: "Costo promedio transporte (COP)" },
      { key: "pregunta_7", header: "¿Cuenta con dispositivos electrónicos?" },
      
      // Incidencias
      { key: "incidencia", header: "Incidencia" },
      { key: "accion_social", header: "Acción Social" },
      { key: "accion_problematica", header: "Acción Problemática" }
    ];

    // 3. Crear workbook y worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Participantes");

    // 4. Agregar columnas dinámicas de asistencias
    const asistenciaCols = allAsistencias.map(nombre => ({
      key: nombre,
      header: nombre
    }));

    worksheet.columns = [...baseHeaders, ...asistenciaCols];

    // 5. Agregar filas con datos transformados
    data.forEach(p => {
      const baseData = {};
      
      // Mapear datos base
      baseHeaders.forEach(col => {
        if (col.key.startsWith('pregunta_')) {
          // Transformar valores de preguntas (1/0 a Sí/No)
          const value = p[col.key];
          if (value === 1) baseData[col.key] = "Sí";
          else if (value === 0) baseData[col.key] = "No";
          else baseData[col.key] = value ?? "";
        } else {
          baseData[col.key] = p[col.key] ?? "";
        }
      });

      // Generar columnas dinámicas de asistencia
      allAsistencias.forEach(nombre => {
        const asistencia = p.children?.find(c => c.nombre === nombre);
        baseData[nombre] = asistencia && asistencia.respuesta === "Si" ? "X" : "";
      });

      worksheet.addRow(baseData);
    });

    // 6. Estilos de encabezados
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { 
        bold: true, 
        color: { argb: "000000" },
        size: 11
      };
      cell.alignment = { 
        vertical: "middle", 
        horizontal: "center", 
        wrapText: true 
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E6E6E6" }, // Gris más claro
      };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    });

    // 7. Ajustar estilo de celdas y ancho dinámico
    worksheet.columns.forEach((col, colIndex) => {
      let maxLength = col.header ? col.header.length : 10;

      col.eachCell({ includeEmpty: true }, cell => {
        const cellValue = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, cellValue.length);
        
        // Alineación centrada para campos específicos
        const centerAlignKeys = [
          "edad", "Asistencias", "pregunta_1", "pregunta_2", "pregunta_3", 
          "pregunta_4", "pregunta_5", "pregunta_7"
        ];
        
        const isCentered = centerAlignKeys.includes(col.key) || 
                          allAsistencias.includes(col.key) ||
                          colIndex >= baseHeaders.length; // Columnas de asistencia
        
        cell.alignment = {
          vertical: "middle",
          horizontal: isCentered ? "center" : "left",
          wrapText: true,
        };
        
        cell.border = {
          top: { style: "thin", color: { argb: "CCCCCC" } },
          left: { style: "thin", color: { argb: "CCCCCC" } },
          bottom: { style: "thin", color: { argb: "CCCCCC" } },
          right: { style: "thin", color: { argb: "CCCCCC" } },
        };
        
        // Formato numérico para la pregunta 6 (costo de transporte)
        if (col.key === "pregunta_6" && cell.value && !isNaN(cell.value)) {
          cell.numFmt = '"$"#,##0';
        }
      });

      // Ajustar ancho de columnas
      if (col.key === "nombre_apellido" || col.key === "correo") {
        col.width = 25;
      } else if (col.key === "pregunta_6") {
        col.width = 20;
      } else if (col.key.startsWith('pregunta_')) {
        col.width = 18;
      } else if (col.key === "accion_problematica" || col.key === "incidencia") {
        col.width = 30;
      } else if (col.key === "accion_social") {
        col.width = 25;
      } else {
        col.width = Math.min(Math.max(maxLength + 2, 12), 50);
      }
    });

    // 8. Congelar la primera fila (encabezados)
    worksheet.views = [
      {
        state: 'frozen',
        xSplit: 0,
        ySplit: 1,
        activeCell: 'A2'
      }
    ];

    // 9. Auto-filtro para todas las columnas
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: worksheet.columnCount }
    };

    // 10. Generar archivo y disparar descarga
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `participantes_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Liberar memoria
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={handleExport}
      style={{
        backgroundColor: '#1890ff',
        borderColor: '#1890ff'
      }}
    >
      Exportar
    </Button>
  );
};

export default ExportParticipantesExcel;